import {
  HTMLProps,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useState,
} from "react";
// icons
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./styles.module.scss";

type Props = {
  title: ReactNode;
  /**
   * toggle settings
   * whether section header is a toggle
   * @default
   */
  isToggle?: boolean;
  /**
   * toggle settings
   * event handler on click the toggle
   */
  onToggle?: (e?: MouseEventHandler<HTMLDivElement>) => void;
  /**
   * toggle settings
   * whether the state is expanded or not
   * @default true
   */
  defaultExpanded?: boolean;
  children?: ReactNode;
} & Omit<HTMLProps<HTMLDivElement>, "title">;

const Section = ({
  title,
  className,
  isToggle = true,
  onToggle,
  defaultExpanded = true,
  children,
  ...rest
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = useCallback(
    (e) => {
      setExpanded((s) => !s);
      if (onToggle) {
        onToggle(e);
      }
    },
    [onToggle],
  );

  return (
    <>
      <div
        className={getClasses(styles["section"], className)}
        {...(isToggle && {
          role: "button",
          onClick: handleToggle,
          "aria-expanded": expanded,
        })}
        {...rest}
      >
        <div>{title}</div>
        {isToggle && (
          <OutlineChevronDown16
            className={getClasses(
              styles["section__caret"],
              !expanded && styles["section__caret--not-show"],
            )}
            aria-hidden={"true"}
          />
        )}
      </div>
      {isToggle ? expanded && children : children}
    </>
  );
};

export default Section;
